// Shared SEO data cache to prevent duplicate API calls
interface SEOCacheEntry {
  data: any;
  timestamp: number;
}

class SEOCache {
  private cache = new Map<string, SEOCacheEntry>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes
  private pendingRequests = new Map<string, Promise<any>>();

  async getSEOData(): Promise<any> {
    const cacheKey = 'seo-data';
    
    console.log('📊 [SEO Cache] Request for SEO data');
    
    // Check if there's a pending request
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      console.log('⏳ [SEO Cache] Returning existing pending request');
      return pendingRequest;
    }

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.TTL) {
      console.log('🚀 [SEO Cache] Returning cached data');
      return cached.data;
    }

    console.log('🔄 [SEO Cache] Creating new API request');
    
    // Create new request
    const requestPromise = this.fetchSEOData();
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const data = await requestPromise;
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } finally {
      // Remove pending request
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async fetchSEOData(): Promise<any> {
    console.log('🔍 [SEO Cache] Making actual API call to /api/seo/data');
    const response = await fetch('/api/seo/data');
    if (!response.ok) {
      throw new Error(`SEO API failed: ${response.status}`);
    }
    const data = await response.json();
    console.log('✅ [SEO Cache] API call completed successfully');
    return data;
  }

  // Clear cache (useful for admin updates)
  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

// Export singleton instance
export const seoCache = new SEOCache();
